import React, { useState } from 'react';
import { toChileTime } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Plus, Pencil, Trash2, Clock,
  ChevronDown, ChevronRight, CornerDownRight,
  DollarSign, ExternalLink, Target
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function AssigneeBadges({ assignees, max }) {
  var m = max || 3;
  if (!assignees || assignees.length === 0) {
    return <span className="text-xs text-gray-400">Sin asignar</span>;
  }
  var shown = assignees.slice(0, m);
  var extra = assignees.length - m;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {shown.map(function(a) {
        if (!a.user) return null;
        return (
          <span key={a.user_id} className="inline-flex px-1.5 py-0.5 bg-gray-100 rounded-full text-[10px] text-gray-600 font-medium">
            {a.user.first_name}
          </span>
        );
      })}
      {extra > 0 && <span className="text-[10px] text-gray-400">+{extra}</span>}
    </div>
  );
}

const STATUS_CFG = {
  pendiente: { label: 'Pendiente', color: 'bg-gray-100 text-gray-600' },
  en_progreso: { label: 'En Progreso', color: 'bg-amber-100 text-amber-700' },
  completada: { label: 'Completada', color: 'bg-emerald-100 text-emerald-700' },
  bloqueada: { label: 'Bloqueada', color: 'bg-rose-100 text-rose-700' },
};

// Flatten task tree for non-recursive rendering
function flattenForList(tasks, depth) {
  var result = [];
  for (var i = 0; i < tasks.length; i++) {
    var t = tasks[i];
    var hasSub = t.subtasks && t.subtasks.length > 0;
    result.push({ task: t, depth: depth, hasSub: hasSub });
    if (hasSub) {
      result = result.concat(flattenForList(t.subtasks, depth + 1));
    }
  }
  return result;
}

export function TaskListView({ tasks, onEditTask, onDeleteTask, onAddSubtask, isJefatura }) {
  const [collapsed, setCollapsed] = useState({});

  if (tasks.length === 0) {
    return (
      <Card className="border-gray-200 bg-white shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <Clock className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-gray-500">No hay tareas</p>
        </CardContent>
      </Card>
    );
  }

  var flatItems = flattenForList(tasks, 0);

  var toggleCollapse = function(taskId) {
    setCollapsed(function(prev) {
      var next = Object.assign({}, prev);
      next[taskId] = !prev[taskId];
      return next;
    });
  };

  // Filter out items whose parents are collapsed
  var isHidden = function(item, idx) {
    // Check all ancestors
    for (var j = idx - 1; j >= 0; j--) {
      var ancestor = flatItems[j];
      if (ancestor.depth < item.depth && collapsed[ancestor.task.id]) {
        return true;
      }
      if (ancestor.depth < item.depth - 1) continue;
      if (ancestor.depth === item.depth) break;
    }
    return false;
  };

  return (
    <Card className="border-gray-200 bg-white shadow-sm" data-testid="task-list-view">
      <CardContent className="p-0">
        <div className="divide-y divide-gray-50">
          {flatItems.map(function(item, idx) {
            if (isHidden(item, idx)) return null;
            var task = item.task;
            var depth = item.depth;
            var hasSub = item.hasSub;
            var isCollapsed = collapsed[task.id];
            var stCfg = STATUS_CFG[task.status] || STATUS_CFG.pendiente;

            var dateStr = '';
            var daysCount = null;
            if (task.start_date) {
              dateStr = format(toChileTime(task.start_date), 'dd MMM', { locale: es });
              if (task.end_date) {
                dateStr += ' - ' + format(toChileTime(task.end_date), 'dd MMM', { locale: es });
                daysCount = Math.max(0, Math.ceil((new Date(task.end_date) - new Date(task.start_date)) / 86400000));
              } else {
                dateStr += ' - ...';
              }
            }

            return (
              <div
                key={task.id}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors"
                style={{ paddingLeft: (12 + depth * 24) + 'px' }}
                data-testid={'task-row-' + task.id}
              >
                <div className="w-5 shrink-0">
                  {hasSub ? (
                    <button onClick={function() { toggleCollapse(task.id); }} className="text-gray-400 hover:text-gray-600">
                      {isCollapsed
                        ? <ChevronRight className="w-4 h-4" />
                        : <ChevronDown className="w-4 h-4" />
                      }
                    </button>
                  ) : depth > 0 ? (
                    <CornerDownRight className="w-3.5 h-3.5 text-gray-300" />
                  ) : null}
                </div>
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: task.color || '#3B82F6' }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {task.code && <span className="font-mono text-[10px] text-gray-400">{task.code}</span>}
                    <span className="font-medium text-sm text-gray-800 truncate">{task.name}</span>
                    <Badge className={stCfg.color + ' text-[10px] shrink-0'}>
                      {stCfg.label}
                    </Badge>
                    {hasSub && (
                      <span className="text-[10px] text-gray-400">
                        ({task.subtasks.length} subtarea{task.subtasks.length > 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <AssigneeBadges assignees={task.assignees} />
                    {dateStr && <span className="text-xs text-gray-400"><Clock className="w-3 h-3 inline mr-0.5" />{dateStr}</span>}
                    {daysCount !== null && <span className="text-xs text-blue-500">{daysCount}d</span>}
                    {task.budget > 0 && <span className="text-xs text-gray-400"><DollarSign className="w-3 h-3 inline" />{new Intl.NumberFormat('es-CL').format(task.budget)}</span>}
                    {task.objective && <span className="text-xs text-gray-400 truncate max-w-[150px]" title={task.objective}><Target className="w-3 h-3 inline mr-0.5" />{task.objective}</span>}
                    {task.url && <a href={task.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline" onClick={(e) => e.stopPropagation()}><ExternalLink className="w-3 h-3 inline" /></a>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="w-14">
                    <Progress value={task.progress} className="h-1.5" />
                  </div>
                  <span className="text-xs text-gray-400 w-7">{task.progress}%</span>
                  <Button
                    variant="ghost" size="icon"
                    className="h-7 w-7 text-gray-400 hover:text-[#7FD02E]"
                    onClick={function() { onAddSubtask(task.id); }}
                    title="Agregar subtarea"
                    data-testid={'add-subtask-' + task.id}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost" size="icon"
                    className="h-7 w-7 text-gray-400 hover:text-[#7FD02E]"
                    onClick={function() { onEditTask(task); }}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {isJefatura && (
                    <Button
                      variant="ghost" size="icon"
                      className="h-7 w-7 text-gray-400 hover:text-destructive"
                      onClick={function() { onDeleteTask(task.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
